import React, { useState } from 'react';
import AdminLayout, { useDateRange } from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { FileText, Download, Loader2, Calendar } from 'lucide-react';

const TEMPLATES = [
  { id: 'users', label: 'Monthly Business Summary', desc: 'All users with segments, plans, streaks, scores, and engagement data.', dataset: 'users' },
  { id: 'audit', label: 'Safety Audit Report', desc: 'Full audit trail of all guardrail events, admin actions, and system events.', dataset: 'audit' },
  { id: 'guardrails', label: 'Guardrail Events Report', desc: 'All blocked messages with domain, user (anonymised), and timestamp.', dataset: 'guardrails' },
  { id: 'subscriptions', label: 'Revenue by Segment', desc: 'Active and cancelled subscriptions with plan details and amounts.', dataset: 'subscriptions' },
  { id: 'waitlist', label: 'Waitlist & Leads Report', desc: 'All pre-launch signups and charter download leads.', dataset: 'waitlist' },
];

const RANGE_LABEL = {
  '7d': 'Last 7 days',
  '14d': 'Last 14 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  'all': 'All time',
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const AdminReports = () => {
  const [generating, setGenerating] = useState(null);
  const [format, setFormat] = useState('csv');
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const { dateRange } = useDateRange();

  const generate = async (template) => {
    setGenerating(template.id);
    setError('');
    try {
      // CC-1 root cause (2026-05): the page previously called
      // `apiClient.request(...)` which always runs `response.json()`. For a
      // CSV response that throws, the catch returned `{detail: 'OK'}`, and
      // the file ended up containing the literal JSON `{"detail":"OK"}` —
      // which Excel renders as a near-blank one-cell sheet. Use raw fetch
      // and pull the body as a `Blob` so the real CSV/JSON bytes reach the
      // user's download.
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      const range = dateRange || 'all';
      const url = `${API_BASE}/api/admin/export/${template.dataset}?format=${format}&range=${encodeURIComponent(range)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Export failed (${res.status}): ${txt.slice(0, 200) || res.statusText}`);
      }
      const blob = await res.blob();
      if (blob.size < 32) {
        // Defensive: backend explicitly returns "No data" (~7 bytes) when a
        // dataset is empty. Surface that to the founder instead of silently
        // writing a tiny file.
        const txt = await blob.text();
        if (txt.trim().toLowerCase() === 'no data') {
          setError(`${template.label}: no rows in the dataset right now.`);
          setGenerating(null);
          return;
        }
      }
      const dl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      const rangeSuffix = range !== 'all' ? `_${range}` : '';
      a.download = `hibff_${template.dataset}${rangeSuffix}_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
      setHistory(prev => [{ name: template.label, date: new Date().toISOString(), format, range, bytes: blob.size }, ...prev]);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Generate failed.');
    }
    setGenerating(null);
  };

  return (
    <AdminLayout pageTitle="Reports">
      <CheatSheet pageId="reports" />
      {/* Active date-range banner — surfaces what window the exports use.
          The pill itself lives in the top header (AdminLayout), but we
          mirror it here so the operator never wonders what scope a
          report is being generated for. */}
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4"
        style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA' }}
        data-testid="reports-range-banner"
      >
        <Calendar size={14} style={{ color: '#F5831F' }} />
        <span className="text-xs" style={{ color: '#9A3412' }}>
          <span className="font-semibold">Date window:</span>{' '}
          <span data-testid="reports-range-label">{RANGE_LABEL[dateRange] || 'All time'}</span>
          {' · '}
          <span className="opacity-75">change via the pill in the top header</span>
        </span>
      </div>

      {/* Format Selector */}
      <div className="flex items-center gap-3 mb-6">
        <span className="text-sm font-medium" style={{ color: '#44403A' }}>Output format:</span>
        {['csv', 'json', 'xlsx', 'pdf'].map(f => (
          <button key={f} onClick={() => setFormat(f)}
            data-testid={`reports-format-${f}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium uppercase ${format === f ? 'text-white' : ''}`}
            style={format === f ? { backgroundColor: '#F5831F' } : { backgroundColor: '#F9F9F9', border: '1px solid #E0E0E0', color: '#44403A' }}>{f}</button>
        ))}
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="reports-error">
          {error}
        </div>
      )}

      {/* Report Templates */}
      <div className="mb-8">
        <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>Report Templates</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TEMPLATES.map(t => (
            <div key={t.id} className="bg-white p-5 rounded-lg flex items-start justify-between" style={{ border: '1px solid #E0E0E0' }} data-testid={`reports-template-${t.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={16} style={{ color: '#F5831F' }} />
                  <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>{t.label}</h3>
                </div>
                <p className="text-xs" style={{ color: '#44403A' }}>{t.desc}</p>
              </div>
              <button onClick={() => generate(t)} disabled={generating === t.id}
                data-testid={`reports-generate-${t.id}`}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 flex-shrink-0 ml-4 disabled:opacity-50"
                style={{ backgroundColor: '#F5831F' }}>
                {generating === t.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Generate
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div>
          <h2 className="text-lg font-bold mb-3" style={{ color: '#1A1209' }}>Recently Generated</h2>
          <div className="bg-white rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid="reports-history">
            {history.map((h, i) => (
              <div key={i} className="p-4 flex items-center justify-between border-b last:border-0" style={{ borderColor: '#E0E0E0' }}>
                <div>
                  <p className="text-sm font-medium" style={{ color: '#333333' }}>{h.name}</p>
                  <p className="text-xs" style={{ color: '#44403A' }}>{h.date.slice(0, 16)} · {h.format.toUpperCase()}{h.range && h.range !== 'all' ? ` · ${h.range}` : ''} · {h.bytes ? `${(h.bytes/1024).toFixed(1)} KB` : ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminReports;
